
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    GET_DASHBOARD_COUNT_DATA_API,
    GET_HIGH_PRIORITY_POS_API,
    GET_VENDOR_WITH_OVERDUE_PO_API
} from '../config/API';
import {
    MdDescription,
    MdFactCheck,
    MdWarning,
    MdBusinessCenter,
    MdVisibility
} from 'react-icons/md';
import Modal from '../components/common/Modal';
import StatCard from '../components/StatCard';
import Pagination from '../components/common/Pagination';
import './Dashboard.css';

const Dashboard = () => {
    const [counts, setCounts] = useState({
        totalPOs: 0,
        pendingVerification: 0,
        overdueVendors: 0,
        activeProjects: 0
    });
    const [pendingDeliveries, setPendingDeliveries] = useState([]);
    const [overdueVendorsList, setOverdueVendorsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingPage, setPendingPage] = useState(1);
    const [pendingTotalPages, setPendingTotalPages] = useState(1);
    const [pendingTotalRecords, setPendingTotalRecords] = useState(0);
    const [overduePage, setOverduePage] = useState(1);
    const [overdueTotalPages, setOverdueTotalPages] = useState(1);
    const [overdueTotalRecords, setOverdueTotalRecords] = useState(0);
    const [selectedItems, setSelectedItems] = useState(null); // { poNumber: '', items: [] }

    const fetchCounts = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const headers = { Authorization: `Bearer ${token}` };
            const countsRes = await axios.get(GET_DASHBOARD_COUNT_DATA_API, { headers });
            if (countsRes.data.status) {
                const data = countsRes.data.data;
                setCounts({
                    pendingVerification: data.reqCount || 0,
                    totalPOs: data.poCount || 0,
                    activeProjects: data.activeProjectCount || 0,
                    overdueVendors: data.overDuePoCount || 0
                });
            }
        } catch (error) {
            console.error('Error fetching counts:', error);
        }
    };

    const fetchHighPriority = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const headers = { Authorization: `Bearer ${token}` };
            const highPriorityRes = await axios.get(`${GET_HIGH_PRIORITY_POS_API}?page=${pendingPage}&limit=5`, { headers });
            if (highPriorityRes.data.status) {
                const rawData = highPriorityRes.data.data || [];
                const mappedData = rawData.map(po => ({
                    id: po.id || po._id,
                    poNumber: po.poNumber || 'N/A',
                    vendor: po.RequestVendor?.Vendor?.vendorName || 'N/A',
                    items: (po.PurchaseOrderItems || []).map(item => item.Product?.productName).filter(Boolean),
                    expected: po.tillDate || 'N/A',
                    status: po.Priority?.priorityName || po.status || 'Pending',
                    statusClass: po.Priority?.priorityName === 'High' || po.Priority?.priorityName === 'Critical' ? 'status-urgent' : 'status-warning'
                }));
                setPendingDeliveries(mappedData);
                setPendingTotalPages(highPriorityRes.data.pagination?.totalPages || 1);
                setPendingTotalRecords(highPriorityRes.data.pagination?.total || highPriorityRes.data.count || 0);
            }
        } catch (error) {
            console.error('Error fetching high priority deliveries:', error);
        }
    };

    const fetchOverdueVendors = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const headers = { Authorization: `Bearer ${token}` };
            const overdueVendorsRes = await axios.get(`${GET_VENDOR_WITH_OVERDUE_PO_API}?page=${overduePage}&limit=5`, { headers });
            if (overdueVendorsRes.data.status) {
                const vendorData = overdueVendorsRes.data.data?.vendors || [];
                const mappedVendors = vendorData.map((v, idx) => {
                    let color = '#f59e0b'; // Default yellow
                    if (v.overdueDays > 45) color = '#b91c1c'; // Dark red
                    else if (v.overdueDays > 15) color = '#dc2626'; // Red
                    else if (v.overdueDays > 7) color = '#d97706'; // Orange

                    return {
                        id: idx,
                        vendor: v.vendorName || 'Unknown Vendor',
                        daysLate: `${v.overdueDays} days late`,
                        color: color
                    };
                });
                setOverdueVendorsList(mappedVendors);
                const pagination = overdueVendorsRes.data.pagination;
                setOverdueTotalPages(pagination?.totalPages || 1);
                setOverdueTotalRecords(pagination?.total || overdueVendorsRes.data.count || 0);
            }
        } catch (error) {
            console.error('Error fetching overdue vendors:', error);
        }
    };

    useEffect(() => {
        const initDashboard = async () => {
            setLoading(true);
            await Promise.all([fetchCounts(), fetchHighPriority(), fetchOverdueVendors()]);
            setLoading(false);
        };
        initDashboard();
    }, []);

    useEffect(() => {
        fetchHighPriority();
    }, [pendingPage]);

    useEffect(() => {
        fetchOverdueVendors();
    }, [overduePage]);

    return (
        <div className="dashboard">
            <h2 className="section-title">Overview</h2>

            <div className="dashboard-grid">
                <StatCard
                    title="Pending Request"
                    value={counts.pendingVerification}
                    to="/generate-requests"
                    icon={<MdFactCheck />}
                    color="#d97706"
                />
                <StatCard
                    title="Total POs"
                    value={loading ? "..." : counts.totalPOs}
                    to="/po-management"
                    icon={<MdDescription />}
                    color="#4f46e5"
                />

                <StatCard
                    title="Overdue Vendors POs"
                    value={loading ? "..." : counts.overdueVendors}
                    to="/po-management"
                    icon={<MdWarning />}
                    color="#dc2626"
                />
                <StatCard
                    title="Active Projects"
                    value={loading ? "..." : counts.activeProjects}
                    to="/projects"
                    icon={<MdBusinessCenter />}
                    color="#059669"
                />
            </div>

            <div className="split-section">
                <div>
                    <h2 className="section-title">High-Priority Pending Deliveries</h2>
                    <div className="card-container">
                        {/* Desktop Table View */}
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>PO Number</th>
                                        <th>Vendor</th>
                                        <th>Items</th>
                                        <th>Expected</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>

                                    {pendingDeliveries.length > 0 ? (
                                        pendingDeliveries.map((delivery) => (
                                            <tr key={delivery.id}>
                                                <td>{delivery.poNumber}</td>
                                                <td>{delivery.vendor}</td>
                                                <td className="items-list-cell">
                                                    {delivery.items.length > 0 ? (
                                                        <button 
                                                            className="view-items-btn"
                                                            onClick={() => setSelectedItems({ poNumber: delivery.poNumber, items: delivery.items })}
                                                        >
                                                            <MdVisibility /> {delivery.items.length} {delivery.items.length === 1 ? 'Item' : 'Items'}
                                                        </button>
                                                    ) : 'N/A'}
                                                </td>
                                                <td>{delivery.expected}</td>
                                                <td><span className={`status-badge ${delivery.statusClass}`}>{delivery.status}</span></td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="no-data">
                                                {loading ? "Loading..." : "No data found"}
                                            </td>
                                        </tr>
                                    )}

                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-card-view">
                            {pendingDeliveries.map((delivery) => (
                                <div key={delivery.id} className="dashboard-card">
                                    <div className="card-header">
                                        <strong>{delivery.poNumber}</strong>
                                        <span className={`status-badge ${delivery.statusClass}`}>{delivery.status}</span>
                                    </div>
                                    <div className="card-body">
                                        <div className="card-row">
                                            <span className="card-label">Vendor:</span>
                                            <span className="card-value">{delivery.vendor}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Items:</span>
                                            <button 
                                                className="view-items-btn"
                                                onClick={() => setSelectedItems({ poNumber: delivery.poNumber, items: delivery.items })}
                                            >
                                                <MdVisibility /> {delivery.items.length} {delivery.items.length === 1 ? 'Item' : 'Items'}
                                            </button>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Expected:</span>
                                            <span className="card-value">{delivery.expected}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {pendingDeliveries.length > 0 && (
                            <Pagination
                                currentPage={pendingPage}
                                totalPages={pendingTotalPages}
                                totalRecords={pendingTotalRecords}
                                onPageChange={(page) => setPendingPage(page)}
                            />
                        )}
                    </div>
                </div>

                <div>
                    <h2 className="section-title">Vendors with Overdue Supply</h2>
                    <div className="card-container">
                        {loading ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                Loading data...
                            </div>
                        ) : overdueVendorsList.length > 0 ? (
                            <>
                                {/* Desktop Table View */}
                                <div className="table-container">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Vendor Name</th>
                                                <th>Days Late</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {overdueVendorsList.map((vendor) => (
                                                <tr key={vendor.id}>
                                                    <td><strong>{vendor.vendor}</strong></td>
                                                    <td style={{ color: vendor.color, fontWeight: 600 }}>{vendor.daysLate}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Mobile Card View */}
                                <div className="mobile-card-view">
                                    {overdueVendorsList.map((vendor) => (
                                        <div key={vendor.id} className="dashboard-card overdue-card">
                                            <div className="card-row">
                                                <strong>{vendor.vendor}</strong>
                                                <span style={{ color: vendor.color, fontWeight: 600 }}>{vendor.daysLate}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {overdueVendorsList.length > 0 && (
                                    <Pagination
                                        currentPage={overduePage}
                                        totalPages={overdueTotalPages}
                                        totalRecords={overdueTotalRecords}
                                        onPageChange={(page) => setOverduePage(page)}
                                    />
                                )}
                            </>
                        ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                                No overdue vendors found
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Items View Modal */}
            <Modal
                isOpen={!!selectedItems}
                onClose={() => setSelectedItems(null)}
                title={`Items for PO: ${selectedItems?.poNumber}`}
                width="400px"
            >
                <div className="items-badges-container">
                    {selectedItems?.items.map((item, idx) => (
                        <span key={idx} className="item-product-badge">
                            {item}
                        </span>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default Dashboard;
